import { Component, OnInit } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { CreerEtiquetteModel } from 'src/models/pilotage/creerEtiquetteModel';
import { EtiquetteService } from 'src/services/etiquette.service';
import { MatDialogRef } from '@angular/material/dialog';
import { UtilisateurService } from 'src/services/utilisateur.service';

@Component({
    selector: 'app-creer-etiquette-dialog',
    imports: [FormsModule, MatProgressSpinnerModule, TranslateModule],
    templateUrl: './creer-etiquette-dialog.component.html',
    styleUrls: ['./creer-etiquette-dialog.component.scss']
})
export class CreerEtiquetteComponent implements OnInit {
  readonly LANGS = ['fr', 'en', 'es'];

  etiquetteLocales: { [key: string]: string } = { fr: '', en: '', es: '' };

  etiquette: CreerEtiquetteModel = {
    locales: this.etiquetteLocales,
    entiteIds: [],
    utilisateurId: 0,
    couleur: '',
    visibleSurCertificats: false
  };

  entitesDisponibles: { id: number, nom: string, estGroupe: boolean }[] = [];
  entitesSelectionnees: { id: number, nom: string, estGroupe: boolean }[] = [];
  entiteIdSelectionnee: number | null = null;
  isLoading: boolean = false;
  estEnCoursDeEnregistrement: boolean = false;
  groupeEntite: { id: number, nom: string, estGroupe: boolean } | null = null;
  affecterAuGroupe: boolean = false;
  affecterAEntite: boolean = false;
  originalEntitesCount: number = 0;
  default_color = '#000000';

  constructor(
    public router: Router,
    public dialogRef: MatDialogRef<CreerEtiquetteComponent>,
    private etiquetteService: EtiquetteService,
    private utilisateurService: UtilisateurService
  ) { }

  ngOnInit(): void {
    this.initializeEntites();
    this.etiquette.couleur = this.default_color;
  }

  initializeEntites(): void {
    const contexte = this.utilisateurService.contexteUtilisateur;
    this.entitesDisponibles = contexte.entitesAccessibles.map(e => ({ id: e.entiteId, nom: e.nom, estGroupe: e.estGroupe }));
    this.groupeEntite = this.entitesDisponibles.find(e => e.estGroupe) || null;
    if (this.groupeEntite) {
      this.entitesDisponibles = this.entitesDisponibles.filter(e => !e.estGroupe);
    }
    this.originalEntitesCount = this.entitesDisponibles.length;
  }

  updateEntitesDisponibles(): void {
    this.entitesDisponibles = this.entitesDisponibles.filter(
      entite => !this.entitesSelectionnees.some(selected => selected.id === entite.id)
    );
  }

  onEntiteSelected(event: Event): void {
    const selectElement = event.target as HTMLSelectElement;
    const entiteId = Number(selectElement.value);
    const entiteSelectionnee = this.entitesDisponibles.find(e => e.id === entiteId);
    if (entiteSelectionnee) {
      this.entitesSelectionnees.push(entiteSelectionnee);
      this.etiquette.entiteIds.push(entiteId);
      this.entitesDisponibles = this.entitesDisponibles.filter(e => e.id !== entiteId);
      selectElement.value = '';
    }
  }

  removeSelectedEntity(id: number): void {
    this.entitesSelectionnees = this.entitesSelectionnees.filter(entite => entite.id !== id);
    this.etiquette.entiteIds = this.etiquette.entiteIds.filter(entiteId => entiteId !== id);
    const removedEntity = this.utilisateurService.contexteUtilisateur.entitesAccessibles.find(e => e.entiteId === id);
    if (removedEntity) {
      this.entitesDisponibles.push({ id: removedEntity.entiteId, nom: removedEntity.nom, estGroupe: removedEntity.estGroupe });
    }
  }

  toggleAffecterAuGroupe(): void {
    this.affecterAuGroupe = !this.affecterAuGroupe;
    if (this.affecterAuGroupe) {
      this.affecterAEntite = false;
      if (this.groupeEntite) {
        this.etiquette.entiteIds = [this.groupeEntite.id];
        this.entitesSelectionnees = [{ id: this.groupeEntite.id, nom: this.groupeEntite.nom, estGroupe: this.groupeEntite.estGroupe }];
      }
      this.entitesDisponibles = this.utilisateurService.contexteUtilisateur.entitesAccessibles
        .filter(e => !e.estGroupe)
        .map(e => ({ id: e.entiteId, nom: e.nom, estGroupe: e.estGroupe }));
    } else {
      this.etiquette.entiteIds = [];
      this.entitesSelectionnees = [];
    }
  }

  toggleAffecterAEntite(): void {
    this.affecterAEntite = !this.affecterAEntite;
    if (this.affecterAEntite) {
      this.affecterAuGroupe = false;
    }
    this.etiquette.entiteIds = [];
    this.entitesSelectionnees = [];
    this.updateEntitesDisponibles();
  }

  boutonSauvegardeDesactive(): boolean {
    const noNameProvided = this.LANGS.every(lang => !this.etiquetteLocales[lang]);
    const noColorProvided = !this.etiquette.couleur;

    if (this.originalEntitesCount === 1) {
      return noNameProvided || noColorProvided;
    } else {
      return noNameProvided || this.etiquette.entiteIds.length === 0 || noColorProvided;
    }
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  saveEtiquette(): void {
    if (!this.boutonSauvegardeDesactive()) {
      this.estEnCoursDeEnregistrement = true;

      const locales: { [key: string]: string } = {};
      for (const lang of this.LANGS) {
        locales[lang] = this.etiquetteLocales[lang] || '';
      }

      const newEtiquette: CreerEtiquetteModel = {
        locales,
        entiteIds: this.originalEntitesCount === 1
          ? [this.entitesDisponibles[0].id]
          : this.etiquette.entiteIds,
        utilisateurId: this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId,
        couleur: this.etiquette.couleur,
        visibleSurCertificats: this.etiquette.visibleSurCertificats
      };

      this.etiquetteService.creerEtiquette(newEtiquette).subscribe({
        next: () => {
          this.estEnCoursDeEnregistrement = false;
          this.dialogRef.close(true);
        },
        error: () => {
          this.estEnCoursDeEnregistrement = false;
        }
      });
    }
  }
}
